import CustomCalendar from '@/components/CustomComponent/CustomCalendar/CustomCalendar';
import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import { COMMON_TEXT } from '@/helpers/common-text';
import {
  MEETING_ROOM_STATUS_BG_COLOR,
  MEMORY_STORE,
  QUERY_STRING_STORE,
  SESSION_STORAGE_KEY,
} from '@/helpers/constants';
import {
  convertArrayToObject,
  generateRandomString,
  sortObjectKeys,
} from '@/helpers/utility';
import {
  addHours,
  addMinutes,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  isSameDay,
  setHours,
  startOfDay,
} from 'date-fns';
import { set as sessionStoreActionSet } from 'forepaas/store/session/action';
// import { createAuthenticatedApiClient } from '@/helpers/api-client'; // TODO: Uncomment when API is ready
import { size } from 'lodash';
import debounce from 'lodash/debounce';
import { Message } from 'primereact/message';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Slider } from 'primereact/slider';
import { Tooltip } from 'primereact/tooltip';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { AvailableIcon, OccupiedIcon, RefreshIcon } from './icons';
import {
  getMeetingRoomOutlookDataByFloorIdListAndDateTimeRange,
  getRoomAvailabilityByFloorIdList,
  getMeetingRoomAnalysisTimeByTenantId,
} from './service';

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class MeetingRoomOutlook extends Component {
  constructor(props) {
    super(props);
    this.state = {
      outlookData: [],
      branch:
        this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0],
      floors: [],
      isLoadingOutlook: false,
      earliestTime: null,
      latestTime: null,
      selectedDate: new Date(),
      //new
      sliderValues: [9, 18],
      selectedTimeRange: {
        from: '09:00',
        to: '18:00',
      },
      nowLineHeight: null, // Dynamic height for now line
    };
    this.pendingRequest = null;
    this.scrollPanelRef = React.createRef();
  }

  componentDidMount() {
    const { onDateChanged, onFloorsChanged } = this.props;
    const { selectedDate, branch } = this.state;
    if (selectedDate) {
      onDateChanged(selectedDate);
    }

    // Always fetch analysis time defaults for the selected date on mount
    // This ensures the slider reflects the correct day's settings
    this.setSliderDefaultsFromAnalysisTime(selectedDate, true);

    const floorIdList = this.getUpdatedFloorIdList({ branchId: branch });
    this.setState({ floors: floorIdList }, () => {
      if (onFloorsChanged) onFloorsChanged(floorIdList);
      this.getMeetingRoomOutlookData();
    });
  }

  // Get day of week key for a given date (mon, tue, wed, etc.)
  getDayKey = (date = new Date()) => {
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    return days[date.getDay()];
  };

  // Set slider defaults from meeting_room_analysis_time table (Block 5, requirement 03_12)
  // If forDate is provided, use that date's day of week; otherwise use the selected date
  setSliderDefaultsFromAnalysisTime = async (forDate = null, updateSessionStore = false) => {
    try {
      const tenantId = this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
      if (!tenantId) {
        // No tenant selected, use default values (0:00 - 24:00)
        return;
      }

      // Fetch analysis time settings from database
      const analysisTime = await getMeetingRoomAnalysisTimeByTenantId({ tenantId });

      if (!analysisTime) {
        // No settings found, use default values (0:00 - 24:00)
        return;
      }

      // Use the provided date or the currently selected date
      const dateToUse = forDate || this.state.selectedDate || new Date();
      const dayKey = this.getDayKey(dateToUse);
      const startTimeStr = analysisTime[`${dayKey}_start`];
      const endTimeStr = analysisTime[`${dayKey}_end`];

      // Parse time string (HH:MM:SS or HH:MM) to hours
      const parseTimeToHours = (timeStr) => {
        if (!timeStr) return null;
        const parts = timeStr.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1] || '0', 10);
        return hours + (minutes / 60);
      };

      const startHours = parseTimeToHours(startTimeStr);
      const endHours = parseTimeToHours(endTimeStr);

      // Validate parsed values (default to 0-24 if invalid)
      // Round to nearest 0.5 (30 minutes) for slider compatibility
      const startTime = (startHours !== null && !isNaN(startHours))
        ? Math.round(startHours * 2) / 2
        : 0;
      const endTime = (endHours !== null && !isNaN(endHours))
        ? Math.round(endHours * 2) / 2
        : 24;

      // Convert to time format (HH:mm) with support for 30-minute intervals
      const formatTime = (hours) => {
        if (hours === 24) return '23:59';
        const h = Math.floor(hours);
        const minutes = (hours % 1) === 0.5 ? 30 : 0;
        return `${h.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      };

      const updatedSliderState = {
        sliderValues: [startTime, endTime],
        selectedTimeRange: {
          from: formatTime(startTime),
          to: formatTime(endTime),
        },
      };

      // Update session store if requested (when date changes)
      if (updateSessionStore) {
        this.props.dispatch(
          sessionStoreActionSet(
            SESSION_STORAGE_KEY.MEETING_ROOM_SLIDER_STATE,
            JSON.stringify(updatedSliderState)
          )
        );
      }

      this.setState(updatedSliderState);
    } catch (error) {
      console.error('Error fetching analysis time for slider defaults:', error);
      // Fall back to default values on error
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const branch =
      this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    const prevBranch =
      prevProps?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0];
    const { selectedDate } = this.state;
    if (branch !== prevBranch) {
      const floorIdList = this.getUpdatedFloorIdList({ branchId: branch });
      this.setState({ branch, floors: floorIdList }, () => {
        if (this.props.onFloorsChanged) this.props.onFloorsChanged(floorIdList);
        this.getMeetingRoomOutlookData();
      });
    } else if (
      selectedDate &&
      prevProps.selectedDate &&
      !isSameDay(selectedDate, prevProps.selectedDate)
    ) {
      this.getMeetingRoomOutlookData();
    }

    // Update now line height when outlook data changes or after initial render
    if (
      prevState &&
      (prevState.outlookData !== this.state.outlookData ||
        prevState.isLoadingOutlook !== this.state.isLoadingOutlook)
    ) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        this.updateNowLineHeight();
      }, 0);
    }
  }

  getUpdatedFloorIdList = ({ branchId }) => {
    const allFloors = this.props.sessionStore?.[MEMORY_STORE.FLOORS] ?? [];
    const floors = allFloors.filter(floor => +floor.branch_id > 0 && +floor.branch_id === +branchId);
    return floors.map(floor => floor.floor_id);
  };

  getMeetingRoomOutlookData = async () => {
    await this.getOutlookData();
  };

  getOutlookData = async () => {
    const { selectedDate, floors } = this.state;
    if (!size(floors) || !selectedDate) {
      return;
    }
    this.setState({ isLoadingOutlook: true });
    try {
      const currentDate = new Date();
      const isCurrentDate = isSameDay(selectedDate, currentDate);
      const startDateTime = format(selectedDate, 'yyyy-MM-dd 00:00:00');
      const endDateTime = format(selectedDate, 'yyyy-MM-dd 23:59:59');
      let outlookData =
        await getMeetingRoomOutlookDataByFloorIdListAndDateTimeRange({
          floorIdList: floors,
          startDateTime: startDateTime,
          endDateTime: endDateTime,
        });
      outlookData = outlookData || [];
      const outlookDataDeviceIdSet = new Set(
        outlookData.map(item => item.device_identifer)
      );
      const rooms = await getRoomAvailabilityByFloorIdList({
        floorIdList: floors,
      });
      let deviceStatusMap = await this.getDeviceStatusMapFromRooms({
        rooms,
      });
      const deviceStatusMapDeviceIdSet = new Set(Object.keys(deviceStatusMap));
      // find device Id that is in deviceStatusMapDeviceIdSet but not in outlookDataDeviceIdSet
      const diffDeviceIdSet = new Set(
        [...deviceStatusMapDeviceIdSet].filter(
          x => !outlookDataDeviceIdSet.has(x)
        )
      );
      outlookData = outlookData.concat(
        [...diffDeviceIdSet].map(device_identifer => ({
          device_identifer,
          room_name: deviceStatusMap[device_identifer]?.['room_name'] ?? '',
          reserved_start: null,
          reserved_end: null,
          occupied_start: null,
          occupied_end: null,
          occupied_minutes: null,
          reserved_minutes: null,
          subject: '',
          organizer: '',
        }))
      );
      // Keep room availability status for all dates, but only show current timestamp for today
      if (!isCurrentDate) {
        const deviceIdKeys = Object.keys(deviceStatusMap);
        for (let i = 0; i < deviceIdKeys.length; i++) {
          const deviceIdKey = deviceIdKeys[i];
          const dev = deviceStatusMap[deviceIdKey];
          if (dev) {
            deviceStatusMap[deviceIdKey] = {
              ...dev,
              room_available_ts: null, // Hide timestamp for non-current dates
            };
          }
        }
      }

      const newOutlookData = outlookData.map(item => {
        return {
          ...item,
          is_room_available:
            deviceStatusMap[item.device_identifer]?.['is_room_available'] ?? -1,
          room_available_ts:
            deviceStatusMap[item.device_identifer]?.['room_available_ts'] ??
            null,
        };
      });
      const newState = {
        outlookData: newOutlookData,
        isLoadingOutlook: false,
      };
      this.setState(newState, () => {
        // Measure and update now line height after data is loaded and rendered
        setTimeout(() => {
          this.updateNowLineHeight();
        }, 0);
        // Notify parent that loading is complete
        if (this.props.onLoadComplete) {
          this.props.onLoadComplete();
        }
      });
    } catch (error) {
      this.setState({ isLoadingOutlook: false }, () => {
        // Notify parent even if there was an error
        if (this.props.onLoadComplete) {
          this.props.onLoadComplete();
        }
      });
    }
  };

  getDeviceStatusMapFromRooms = async ({ rooms }) => {
    let deviceStatusMap = {};
    try {
      const deviceStatusList = rooms?.map(item => ({
        device_identifer: item.device_identifer,
        is_room_available: item.room_availability === 1,
        room_available_ts: item.timestamp,
        room_name: item.device_name,
      }));
      deviceStatusMap = deviceStatusList.reduce((acc, curr) => {
        acc[curr.device_identifer] = {
          room_name: curr.room_name,
          is_room_available: curr.is_room_available,
          room_available_ts: curr.room_available_ts,
        };
        return acc;
      }, {});
    } catch (error) {
      console.error(error);
    }
    return deviceStatusMap;
  };

  renderTooltipContent(tooltipData) {
    const {
      subject,
      reserved_start,
      reserved_end,
      occupied_minutes,
      reserved_minutes,
      organizer,
    } = tooltipData;
    const reservedStartTime = reserved_start?.split(' ')?.[1]?.substring(0, 5);
    const reservedEndTime = reserved_end?.split(' ')?.[1]?.substring(0, 5);
    return (
      <div className="tooltip-content">
        <div className="tooltip-content-item">
          <div>{`${reservedStartTime} - ${reservedEndTime}`}</div>
        </div>
        <div className="tooltip-content-item">
          <div>{`(${occupied_minutes ?? '-'} / ${reserved_minutes})`}</div>
        </div>
        <div className="tooltip-content-item subject">
          <div>
            <b>{subject}</b>
          </div>
        </div>
        <div className="tooltip-content-item">
          <div>{organizer}</div>
        </div>
      </div>
    );
  }

  getEarliestAndLatestTime(arr) {
    let earliestTime = null;
    let latestTime = null;

    arr.forEach(item => {
      if (item.reserved_start) {
        const reservedStart = new Date(item.reserved_start);
        earliestTime = !earliestTime
          ? reservedStart
          : isBefore(reservedStart, earliestTime)
            ? reservedStart
            : earliestTime;
        latestTime = !latestTime
          ? reservedStart
          : isAfter(reservedStart, latestTime)
            ? reservedStart
            : latestTime;
      }

      if (item.reserved_end) {
        const reservedEnd = new Date(item.reserved_end);
        earliestTime = !earliestTime
          ? reservedEnd
          : isBefore(reservedEnd, earliestTime)
            ? reservedEnd
            : earliestTime;
        latestTime = !latestTime
          ? reservedEnd
          : isAfter(reservedEnd, latestTime)
            ? reservedEnd
            : latestTime;
      }

      if (item.occupied_start) {
        const occupiedStart = new Date(item.occupied_start);
        earliestTime = !earliestTime
          ? occupiedStart
          : isBefore(occupiedStart, earliestTime)
            ? occupiedStart
            : earliestTime;
        latestTime = !latestTime
          ? occupiedStart
          : isAfter(occupiedStart, latestTime)
            ? occupiedStart
            : latestTime;
      }

      if (item.occupied_end) {
        const occupiedEnd = new Date(item.occupied_end);
        earliestTime = !earliestTime
          ? occupiedEnd
          : isBefore(occupiedEnd, earliestTime)
            ? occupiedEnd
            : earliestTime;
        latestTime = !latestTime
          ? occupiedEnd
          : isAfter(occupiedEnd, latestTime)
            ? occupiedEnd
            : latestTime;
      }
    });

    const defaultEarliestTime = setHours(startOfDay(earliestTime), 9);
    const defaultLatestTime = setHours(startOfDay(latestTime), 18);

    if (isAfter(earliestTime, defaultEarliestTime)) {
      earliestTime = defaultEarliestTime;
    }

    if (isBefore(latestTime, defaultLatestTime)) {
      latestTime = defaultLatestTime;
    }

    return { earliestTime, latestTime };
  }

  calculateNowLinePosition(startDate, endDate) {
    const formattedStartDate = format(startDate, 'yyyy-MM-dd HH:mm');
    const formattedEndDate = format(endDate, 'yyyy-MM-dd HH:mm');
    // Create a Date object representing the start of today
    const today = startOfDay(new Date());

    // Get the current time in hours and minutes
    const hours = new Date().getHours();
    const minutes = new Date().getMinutes();
    // Add the current time to the start of today to get the current time as a Date object
    const now = addMinutes(addHours(today, hours), minutes);

    const totalMinutes = differenceInMinutes(
      new Date(formattedEndDate),
      new Date(formattedStartDate)
    );
    const isSameDayWithToday =
      isSameDay(new Date(formattedStartDate), new Date(formattedEndDate)) &&
      isSameDay(new Date(formattedStartDate), today);

    // Calculate the number of minutes that have passed since start time
    // If today is not the same day as the start and end dates, set elapsedMinutes to -1
    let elapsedMinutes = -1;
    if (isSameDayWithToday) {
      const currentTime = new Date(format(now, 'yyyy-MM-dd HH:mm'));
      const startTime = new Date(formattedStartDate);
      const endTime = new Date(formattedEndDate);

      // Check if current time is within the selected time range
      if (isBefore(currentTime, startTime) || isAfter(currentTime, endTime)) {
        // Current time is outside the selected range, don't show the now line
        elapsedMinutes = -1;
      } else {
        elapsedMinutes = differenceInMinutes(currentTime, startTime);
      }
    }

    // Calculate the percentage of time that has passed between start and end times
    // Ensure the percentage is between 0 and 100
    let percentageElapsed = -1;
    if (elapsedMinutes >= 0) {
      percentageElapsed = Math.min(Math.max((elapsedMinutes / totalMinutes) * 100, 0), 100);
    }

    return {
      percentageElapsed,
      nowTimeString: format(now, 'HH:mm'),
    };
  }

  // Measure the actual height of the ScrollPanel content and update now line height
  updateNowLineHeight = () => {
    if (this.scrollPanelRef.current) {
      // Access the ScrollPanel's content element
      const scrollPanelElement = this.scrollPanelRef.current.getElement();
      if (scrollPanelElement) {
        // Find the actual content container inside ScrollPanel
        const contentElement = scrollPanelElement.querySelector('.p-scrollpanel-content');
        if (contentElement) {
          const actualHeight = contentElement.scrollHeight || contentElement.offsetHeight;
          // Add some padding to account for the top offset
          const nowLineHeight = actualHeight + 50;
          if (this.state.nowLineHeight !== nowLineHeight) {
            this.setState({ nowLineHeight });
          }
        }
      }
    }
  };

  renderNowLine(start, end, dataByRoomNameCount) {
    let { percentageElapsed: nowLineLeftPosition, nowTimeString } =
      this.calculateNowLinePosition(start, end);
    if (nowLineLeftPosition >= 0) {
      // Use measured height if available, otherwise fall back to calculated height
      const { nowLineHeight } = this.state;
      let calculatedHeight = nowLineHeight !== null
        ? nowLineHeight
        : dataByRoomNameCount * 30 + 50;

      let nowLineTopPosition = -20;
      return (
        <div
          className="now-line"
          style={{
            left: `${`${nowLineLeftPosition}%`}`,
            height: `${calculatedHeight}px`,
            top: `${nowLineTopPosition}px`,
          }}
        >
          <div className="now-line-tooltip">{`${nowTimeString}`}</div>
        </div>
      );
    }
    return <></>;
  }

  calculateOffset(
    reserved_start,
    reserved_end,
    occupied_start,
    occupied_end,
    start,
    end
  ) {
    const st = new Date(reserved_start).getTime();
    const et = new Date(reserved_end).getTime();
    const ost = occupied_start ? new Date(occupied_start).getTime() : null;
    const oet = occupied_end ? new Date(occupied_end).getTime() : null;

    const endMinusStart = end - start;

    // Calculate the visible portion of the reservation within the selected time range
    const visibleStart = Math.max(st, start);
    const visibleEnd = Math.min(et, end);

    // If reservation is completely outside the range, return zero dimensions
    if (visibleEnd <= visibleStart) {
      return {
        reservedWidth: 0,
        reservedLeftOffset: 0,
        occupiedWidth: 0,
        occupiedLeftOffset: 0,
      };
    }

    // Calculate width and position for the visible portion
    const width = ((visibleEnd - visibleStart) / endMinusStart) * 100;
    const leftOffset = ((visibleStart - start) / endMinusStart) * 100;

    // Calculate occupied portion (only if it overlaps with visible range)
    let occupiedWidth = 0;
    let occupiedLeftOffset = 0;
    if (ost && oet) {
      const occupiedVisibleStart = Math.max(ost, visibleStart);
      const occupiedVisibleEnd = Math.min(oet, visibleEnd);

      if (occupiedVisibleEnd > occupiedVisibleStart) {
        // Calculate occupied width relative to the visible reservation width
        const visibleReservationWidth = visibleEnd - visibleStart;
        occupiedWidth = ((occupiedVisibleEnd - occupiedVisibleStart) / visibleReservationWidth) * 100;
        // Calculate occupied left offset relative to the visible reservation start
        occupiedLeftOffset = ((occupiedVisibleStart - visibleStart) / visibleReservationWidth) * 100;
      }
    }

    return {
      reservedWidth: width,
      reservedLeftOffset: leftOffset,
      occupiedWidth: occupiedWidth,
      occupiedLeftOffset: occupiedLeftOffset,
    };
  }

  renderMessageError = () => {
    const { branch } = this.state;
    if (!branch) {
      return (
        <Message
          severity="error"
          text={COMMON_TEXT.BRANCH_NOT_SELECTED_PLEASE_SELECT}
        />
      );
    }
  };

  renderRefreshButton() {
    const { isLoadingOutlook, branch } = this.state;
    return (
      <button
        className="refresh-button"
        disabled={isLoadingOutlook || !branch}
        onClick={() => this.getMeetingRoomOutlookData()}
      >
        <RefreshIcon size={16} />
      </button>
    );
  }

  datePickerOnChanged = e => {
    const BOUNCE_IN_MILISECONDS = 300;
    const { selectedDate } = this.state;
    const newDate = e.value;

    if (!isSameDay(newDate, selectedDate)) {
      // Check if the day of week changed (e.g., Monday → Tuesday)
      const oldDayKey = this.getDayKey(selectedDate);
      const newDayKey = this.getDayKey(newDate);
      const dayOfWeekChanged = oldDayKey !== newDayKey;

      const debouncedFunc = debounce(() => {
        // passing data back to parent
        const { onDateChanged } = this.props;
        onDateChanged(newDate);
        this.getMeetingRoomOutlookData();

        // If day of week changed, update slider defaults from analysis time settings
        if (dayOfWeekChanged) {
          this.setSliderDefaultsFromAnalysisTime(newDate, true);
        }
      }, BOUNCE_IN_MILISECONDS);
      if (this.pendingRequest) {
        // Cancel any pending requests
        clearTimeout(this.pendingRequest);
      }
      // Schedule the debounced function to run after 500ms
      this.pendingRequest = setTimeout(() => {
        debouncedFunc();
        this.pendingRequest = null;
      }, BOUNCE_IN_MILISECONDS);
    }

    this.setState({ selectedDate: newDate });
  };

  renderDatePicker() {
    const { selectedDate } = this.state;
    const maxDate = new Date();
    return (
      <CustomCalendar
        value={selectedDate}
        onChange={this.datePickerOnChanged}
        maxDate={maxDate}
        label=""
        style={{ marginRight: '10px' }}
      ></CustomCalendar>
    );
  }

  renderReverseOccupiedContainer(roomData, start, end) {
    // Filter reservations that overlap with the selected time range
    const filteredRoomData = roomData.filter(item => {
      if (!item.reserved_start || !item.reserved_end) return false;
      const reservedStart = new Date(item.reserved_start).getTime();
      const reservedEnd = new Date(item.reserved_end).getTime();
      // Check if reservation overlaps with the selected time range
      return reservedEnd > start && reservedStart < end;
    });

    return (
      <div className="reverse-occupied-container">
        {filteredRoomData.map(item => {
          const {
            reserved_start,
            reserved_end,
            occupied_start,
            occupied_end,
            occupied_minutes,
          } = item;
          const {
            reservedWidth,
            reservedLeftOffset,
            occupiedWidth,
            occupiedLeftOffset,
          } = this.calculateOffset(
            reserved_start,
            reserved_end,
            occupied_start,
            occupied_end,
            start,
            end
          );
          // Don't render if the reservation is completely outside the visible range
          if (reservedWidth <= 0 || reservedLeftOffset >= 100) {
            return null;
          }
          const targetId = generateRandomString(8);

          const childDiv = (
            <Fragment key={`child-div-${targetId}`}>
              <Tooltip
                className="child-div-tooltip"
                target={`.child-div-tooltip-target-${targetId}`}
                baseZIndex={2000}
                autoHide={false}
                position="top"
                event="hover"
              >
                {this.renderTooltipContent(item)}
              </Tooltip>
              <div
                className={`child-div child-div-tooltip-target-${targetId}`}
                style={{
                  width: `${reservedWidth}%`,
                  left: `${reservedLeftOffset}%`,
                  backgroundColor:
                    occupied_minutes === null ||
                      occupied_minutes.toString().trim() === ''
                      ? MEETING_ROOM_STATUS_BG_COLOR.NOT_FINISH_BG_COLOR
                      : MEETING_ROOM_STATUS_BG_COLOR.RESERVED_BG_COLOR,
                }}
              >
                {/* <div className="meeting-name">{subject}</div> */}
                {occupied_start && occupied_end && (
                  <div
                    className="occupied-child-div"
                    style={{
                      width: `${occupiedWidth}%`,
                      left: `${occupiedLeftOffset}%`,
                      backgroundColor:
                        MEETING_ROOM_STATUS_BG_COLOR.OCCUPIED_BG_COLOR,
                    }}
                  />
                )}
              </div>
            </Fragment>
          );

          return childDiv;
        })}
      </div>
    );
  }

  getLabelTimeList = ({ fromTimeStr, toTimeStr }) => {
    // Map the numeric values to time strings (supports 30-minute intervals)
    const convertTimeStringsToValues = times => {
      return times.map(time => {
        const [hours, minutes = '0'] = time.split(':');
        const hoursNum = parseInt(hours, 10);
        const minutesNum = parseInt(minutes, 10);
        return hoursNum + (minutesNum / 60);
      });
    };
    const convertValuesToTimeStrings = val => {
      const hours = Math.floor(val);
      const minutes = Math.round((val - hours) * 60);
      const formattedHours = hours < 10 ? '0' + hours : hours;
      const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
      return `${formattedHours}:${formattedMinutes}`;
    };
    const numericValues = convertTimeStringsToValues([fromTimeStr, toTimeStr]);
    const labels = [];
    const diff = numericValues[1] - numericValues[0];
    const numLabels = 5;

    // Generate 5 evenly spaced labels
    for (let i = 0; i < numLabels; i++) {
      const value = numericValues[0] + (diff * i) / (numLabels - 1);
      const timeStr = convertValuesToTimeStrings(value);
      const leftPosition = (i / (numLabels - 1)) * 100;
      labels.push({ time: timeStr, leftPosition });
    }
    return labels;
  };

  renderMeetingOutlook({ outlookData }) {
    if (!outlookData || outlookData.length === 0) {
      return <div className="no-data-container">
        {COMMON_TEXT.NO_DATA}
      </div>;
    }
    const { selectedDate, selectedTimeRange } = this.state;
    // Robust date comparison for today
    const now = new Date();
    const isToday = selectedDate &&
      selectedDate.getFullYear() === now.getFullYear() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getDate() === now.getDate();
    const fromTimeStr = selectedTimeRange.from;
    let toTimeStr = selectedTimeRange.to;
    let formatStart = 'yyyy-MM-dd ' + fromTimeStr;
    let formatEnd = 'yyyy-MM-dd ' + toTimeStr;
    let timelabels = this.getLabelTimeList({
      fromTimeStr,
      toTimeStr,
    });
    const formattedStartDate = format(selectedDate, formatStart);
    const formattedEndDate = format(selectedDate, formatEnd);

    const dataByRoomName = sortObjectKeys(
      convertArrayToObject(outlookData, 'room_name')
    );

    return (
      <div className="main-outside-div-container">
        <div key="empty-checkbox" className="main-div-item-container view-all">
          {this.renderSliderTimeRange()}
        </div>
        <div key="empty" className="main-div-item-container empty-container">
          <div className="room-name">{''}</div>
          <div className="main-div-container">
            {timelabels.map(({ time, leftPosition }) => (
              <div
                key={time}
                className="time-label"
                style={{ left: `calc(${leftPosition}% - 25px)` }}
              >
                {time}
              </div>
            ))}
            {this.renderNowLine(
              new Date(formattedStartDate).getTime(),
              new Date(formattedEndDate).getTime(),
              Object.keys(dataByRoomName).length
            )}
          </div>
        </div>
        <ScrollPanel
          ref={this.scrollPanelRef}
          style={{ width: '100%', height: 'fit-content' }}
        >
          {Object.keys(dataByRoomName).map(roomName => {
            const roomData = dataByRoomName[roomName];
            let is_room_available = false;
            let room_available_ts = null;
            if (roomData?.length > 0) {
              is_room_available = roomData[0]['is_room_available'];
              room_available_ts = roomData[0]['room_available_ts'] ?? '-';
            }
            const targetId = generateRandomString(8);
            return (
              <div key={roomName} className="main-div-item-container">
                <div className="room-name">
                  <span className="text-left">{roomName}</span>
                  {isToday && is_room_available >= 0 ? (
                    <>
                      <div
                        className={`room-status-icon room-available-target-${targetId}`}
                      >
                        {is_room_available ? <AvailableIcon /> : <OccupiedIcon />}
                      </div>
                      {room_available_ts && (
                        <Tooltip
                          className="child-div-tooltip"
                          target={`.room-available-target-${targetId}`}
                          baseZIndex={2000}
                          autoHide={false}
                          position="right"
                          event="hover"
                        >
                          <div className="tooltip-content">
                            <div className="tooltip-content-item">
                              <div>{COMMON_TEXT.LAST_UPDATED_TIME}</div>
                              <div>{room_available_ts}</div>
                            </div>
                          </div>
                        </Tooltip>
                      )}
                    </>
                  ) : null}
                </div>
                <div className="main-div-container">
                  {this.renderReverseOccupiedContainer(
                    roomData,
                    new Date(formattedStartDate).getTime(),
                    new Date(formattedEndDate).getTime()
                  )}
                </div>
              </div>
            );
          })}
        </ScrollPanel>
      </div>
    );
  }

  renderSliderTimeRange() {
    // Map the numeric values to time strings (supports 30-minute intervals)
    const convertSliderValuesToTimeStrings = vals => {
      return vals.map(val => {
        if (val === 24) {
          return '23:59';
        }
        const hours = Math.floor(val);
        const minutes = (val % 1) === 0.5 ? 30 : 0;
        const formattedHours = hours < 10 ? '0' + hours : hours;
        const formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
        return `${formattedHours}:${formattedMinutes}`;
      });
    };

    const handleSliderChange = e => {
      if (e.value[0] > e.value[1]) {
        return;
      }
      const timeStrings = convertSliderValuesToTimeStrings(e.value);
      const updatedSliderState = {
        sliderValues: e.value,
        selectedTimeRange: {
          from: timeStrings[0],
          to: timeStrings[1],
        },
      };
      this.props.dispatch(
        sessionStoreActionSet(
          SESSION_STORAGE_KEY.MEETING_ROOM_SLIDER_STATE,
          JSON.stringify(updatedSliderState)
        )
      );
      this.setState(updatedSliderState);
    };

    const { sliderValues, selectedTimeRange } = this.state;

    return (
      <div className="outlook-title-container">
        <div className="slider-container">
          <Slider
            value={sliderValues}
            onChange={handleSliderChange}
            range
            min={0}
            max={24}
            step={0.5}
          />
          <div
            className="time-slider-tooltip-start"
            style={{ left: `calc(${(sliderValues[0] / 24) * 100}% - 1.2rem)` }}
          >
            {selectedTimeRange.from}
          </div>
          <div
            className="time-slider-tooltip-end"
            style={{ left: `calc(${(sliderValues[1] / 24) * 100}% - 1.2rem)` }}
          >
            {selectedTimeRange.to}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isLoadingOutlook, outlookData } = this.state;

    return (
      <div className="network-container meeting-room-outlook">
        <div className="network-title-container">
          <div className="text-left">
            <div>予約状況</div>
          </div>
          <div className="grid grid-nogutter align-items-center justify-content-end">
            {this.renderDatePicker()}
            {this.renderRefreshButton()}
          </div>
        </div>
        <div className="network-content">
          {this.renderMessageError() || (
            <>
              <div className="outlook-container">
                {isLoadingOutlook ? (
                  <LoadingSpinner />
                ) : (
                  <>{this.renderMeetingOutlook({ outlookData })}</>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

MeetingRoomOutlook.propTypes = {
  onDateChanged: PropTypes.func,
  onLoadComplete: PropTypes.func,
  onFloorsChanged: PropTypes.func,
};

export default MeetingRoomOutlook;
